import {
  Avatar,
  Box,
  CloseButton,
  Drawer,
  Flex,
  IconButton,
  Image,
  Portal,
  Spacer,
  Tag,
  Text,
} from "@chakra-ui/react"
import { useQueryClient } from "@tanstack/react-query"
import { Link } from "@tanstack/react-router"
import { FiLogOut, FiMenu, FiUser } from "react-icons/fi"

import Logo from "../../../public/assets/images/logo-hermes.svg"
import type { UserPreview } from "../../client"
import useAuth from "../../hooks/useAuth"
import SidebarItems from "./SidebarItems"
import { useState } from "react"
import UserMenu from "./UserMenu"
import { useSettings } from "../UI/settings-context"

const ENVIRONMENT = import.meta.env.VITE_ENVIRONMENT


const Sidebar = () => {
  const queryClient = useQueryClient()
  const currentUser = queryClient.getQueryData<UserPreview>(["currentUser"])

  const [open, setIsOpen] = useState(false)
  const onClose = () => setIsOpen(false)

  const { logout } = useAuth()

  const handleLogout = async () => {
    logout()
  }

  const settings = useSettings()

  return (
    <>
      {/* Mobile */}
      <Drawer.Root placement="start" open={open} onOpenChange={(e) => setIsOpen(e.open)}>
        <Drawer.Trigger asChild>
          <IconButton
            display={{ base: "flex", md: "none" }}
            aria-label="Open Menu"
            position="fixed"
            size="md"
            top={ENVIRONMENT == "staging" ? 20 : 4}
            left={5}
            zIndex={2}
            bg="main"
            color="white"
          >
            <FiMenu />
          </IconButton>
        </Drawer.Trigger>
        <Portal>
          <Drawer.Backdrop />
          <Drawer.Positioner>
            <Drawer.Content maxW="full">
              <Drawer.CloseTrigger asChild>
                <CloseButton size="md" bg="main" color="white" />
              </Drawer.CloseTrigger>

              <Drawer.Body py={8}>
                <Flex flexDir="column" justify="space-between" alignItems="center" h="full">
                  <Box>
                    <Flex maxH="20em">
                      <Image
                        src={settings?.logo ? settings.logo.path.replace(/-(LG)\./, "-MD.") : Logo}
                        alt="Logo"
                        p={6}
                        w="sm"
                        objectFit="contain"
                      />
                    </Flex>
                    <UserMenu onClose={onClose} />

                    <SidebarItems onClose={onClose} />

                    <Flex
                      as="button"
                      onClick={handleLogout}
                      p={4}
                      color="danger"
                      fontWeight="bold"
                      alignItems="center"
                      fontSize="18px"
                    >
                      <FiLogOut />
                      <Text ml={2} bottom="0">
                        Abmelden
                      </Text>
                    </Flex>
                  </Box>
                  {currentUser?.email && (
                    <Box display="flex" flexDir="column" alignItems="center">
                      <Text
                        color={{_light: "black", _dark: "white"}}
                        lineClamp={2}
                        fontSize="sm"
                        p={2}
                      >
                        Angemeldet als:
                      </Text>

                      <Spacer w={2} />

                      <Link to="/usersettings">
                        <Tag.Root size="sm" bg="main" borderRadius="full" color="white">
                          <FiUser />
                          <Spacer w={2} />
                          <Tag.Label>
                            {currentUser.lastname
                              ? currentUser.lastname
                              : currentUser.email}
                          </Tag.Label>
                        </Tag.Root>
                      </Link>
                    </Box>
                  )}
                </Flex>
              </Drawer.Body>
            </Drawer.Content>
          </Drawer.Positioner>
        </Portal>
      </Drawer.Root>

      {/* Desktop */}
      <Box
        bg={{_light: "light"}}
        p={3}
        h="100vh"
        position="sticky"
        top="0"
        display={{ base: "none", md: "flex" }}
      >
        <Flex
          flexDir="column"
          justify="space-between"
          bg={{_light: "secondary", _dark: "darkSlate"}}
          p={4}
          borderRadius={12}
          w="22em"
        >
          <Box>
            <Flex justifyContent="center">
              <Link to="/">
                <Image
                  src={settings?.logo ? settings.logo.path.replace(/-(LG)\./, "-MD.") : Logo}
                  alt="Logo"
                  maxW="xs"
                  maxH="15em"
                  p={6}
                  w="100%"
                  h="100%"
                />
              </Link>
            </Flex>
            <SidebarItems />
          </Box>
          {currentUser?.email && (
            <Flex flexDir="column" alignItems="center" spaceY={2}>
              <Text>Angemeldet als:</Text>
              <Link to="/usersettings">
                <Tag.Root
                  size="xl"
                  bg="main"
                  borderRadius="full"
                  color="white"
                  >
                  <Avatar.Root size="xs" bgColor="transparent" p={1}>
                    <Avatar.Fallback
                      _light={{ color: "white" }}
                      as={FiUser}
                    />
                    <Avatar.Image src={currentUser.fk_media_profile_pic?.path.replace(/-(LG)\./, "-SM.")} />
                  </Avatar.Root>

                  <Tag.Label fontSize="md">{`${currentUser.firstname ?? ""} ${currentUser.lastname ?? ""}`}</Tag.Label>
                </Tag.Root>
              </Link>
            </Flex>
          )}
        </Flex>
      </Box>
    </>
  )
}

export default Sidebar
